import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Button, Toast, Form } from 'react-bootstrap';
import { useRefreshSingleMealAndGroceryList } from '../hooks/refreshSingleMealAndGroceryList';
import { useUserShowMealDeletionConfirmation } from '../hooks/userPreferencesHelper';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAddSingleMealToMealPlan } from '../hooks/addSingleMealToMealPlan';
import { useAddRecipeToMealPlan } from '../hooks/useAddRecipeToMealPlan';
import AddToMealPlanModal from './AddToMealPlanModal';
import { getRecipeImageUrl, getS3ImageUrl } from '../utils/imageUtils';



function MealCard({ meal, onStartCooking, isInPast, onMealRefresh, onGroceryListRefresh }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showModal, setShowModal] = useState(false);
    const [servingSize, setServingSize] = useState(meal.serving_size || 1);
    const [errorRefreshingMeal, setErrorRefreshingMeal] = useState(false);
    const [errorRefreshingMealMessage, setErrorRefreshingMealMessage] = useState('');
    const [servingSizeUpdateTimeout, setServingSizeUpdateTimeout] = useState(null);
    const [isUpdatingServingSize, setIsUpdatingServingSize] = useState(false);
    const navigate = useNavigate();
    const { refreshSingleMealAndGroceryList, isRefreshing, error } = useRefreshSingleMealAndGroceryList();
    const { pgUser, sessionToken, updatePgUserProperty } = useAuth();
    const { updateUserShowMealDeletionConfirmation, isLoading: isUpdatingShowMealDeletionConfirmation } = useUserShowMealDeletionConfirmation();
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(pgUser?.show_meal_deletion_confirmation_modal ?? true);
    const { addSingleMealToMealPlan, isLoading: isAddingSingleMeal, error: addSingleMealError } = useAddSingleMealToMealPlan();
    const [recentlyDeletedMealObjectArray, setRecentlyDeletedMealObjectArray] = useState([]); //for storing the meal object that was deleted from the meal plan, in case user wants to undo the deletion
    //recentlyDeletedMealObject stores the following properties: meal_id, recipe_id, serving_size, meal_of_day, planned_cook_date, actual_cook_date, has_been_cooked, is_favorite
    
    // Add to Meal Plan modal state and hook
    const [showAddToMealPlanModal, setShowAddToMealPlanModal] = useState(false);
    const [addToMealPlanServings, setAddToMealPlanServings] = useState(meal.serving_size || 1);
    const { addRecipeToMealPlan, isLoading: isAddingToMealPlan, error: addToMealPlanError } = useAddRecipeToMealPlan();

    // Sync local state with pgUser when it changes
    useEffect(() => {
        if (pgUser?.show_meal_deletion_confirmation_modal !== undefined) {
            setShowDeletionConfirmation(pgUser.show_meal_deletion_confirmation_modal);
        }
    }, [pgUser?.show_meal_deletion_confirmation_modal]);

    // Debounced serving size update
    useEffect(() => {
        if (servingSizeUpdateTimeout) {
            clearTimeout(servingSizeUpdateTimeout);
        }

        if (servingSize !== meal.serving_size) {
            const timeout = setTimeout(async () => {
                await handleServingSizeUpdate();
            }, 500); // Wait 0.5 second after last change
            setServingSizeUpdateTimeout(timeout);
        }

        return () => {
            if (servingSizeUpdateTimeout) {
                clearTimeout(servingSizeUpdateTimeout);
            }
        };
    }, [servingSize, meal.serving_size]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (servingSizeUpdateTimeout) {
                clearTimeout(servingSizeUpdateTimeout);
            }
        };
    }, []);

    const handleCardClick = () => {
        // navigate(`/recipes/${meal.recipe_id}/instructions`);
    };

    const showErrorRefreshingMealToast = (message) => {
        setErrorRefreshingMealMessage(message);
        setErrorRefreshingMeal(true);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            setErrorRefreshingMeal(false);
        }, 5000);
    };

    const handleStartCooking = (e) => {
        e.stopPropagation();
        if (!meal.has_been_cooked) {
            // Navigate to meal plan with mealId parameter to show meal steps
            navigate(`/meal-plan?mealId=${meal.id}`);
            // Call parent callback to set selected meal
            if (onStartCooking) {
                onStartCooking(meal);
            }
        }
    };

    const handleModalClose = () => setShowModal(false);
    const handleModalShow = (e) => {
        e.stopPropagation();
        if (!meal.has_been_cooked && !isInPast) {
            setShowModal(true);
        }
    };

    const handleTrashClick = (e) => {
        e.stopPropagation();
        if (!meal.has_been_cooked && !isInPast) {
            // Check if user wants to see confirmation modal
            if (showDeletionConfirmation) {
                setShowModal(true);
            } else {
                // Skip modal and delete directly
                handleDeleteConfirm();
            }
        }
    };

    const handleRefreshMeal = async (e) => {
        e.stopPropagation();

        if (!sessionToken) {
            throw new Error('No valid session found');
        }

        if (!meal.has_been_cooked && !isInPast) {
            try {
                // Wait for the backend to complete the meal refresh
                await refreshSingleMealAndGroceryList(meal.id, meal.app_user_id);
                // Only call onMealRefresh after the backend has finished
                if (onMealRefresh) {
                    onMealRefresh();
                }
                // Show toast notification
                if (onGroceryListRefresh) {
                    onGroceryListRefresh("Grocery list updated");
                }
            } catch (error) {
                console.error('Failed to refresh meal:', error);
                showErrorRefreshingMealToast('Error refreshing meal. Please refresh the page and try again.');
            }
        }
    };

    const handleDeleteConfirm = async () => {
        
        try {        
            
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            console.log("about to call delete_and_refresh with meal_id: ", meal.id, " and appUserId: ", pgUser.id + " and sessionToken: ", sessionToken);
            
            const response = await fetchWithCSRF('/api/v1/meal/delete_and_refresh', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    meal_id: meal.id,
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to remove meal');
            }

            console.log('Meal recipe removed:', data);
            setRecentlyDeletedMealObjectArray([...recentlyDeletedMealObjectArray, meal]);
            handleModalClose();
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification with undo button
            if (onGroceryListRefresh) {
                onGroceryListRefresh(<>Grocery list updated <button onClick={() => handleUndoDelete([meal])} className="btn btn-link align-baseline" style={{ textDecoration: 'none', color: '#fff', border: '1px solid #fff', padding: '2px 8px', borderRadius: '4px' }}>Undo</button></>);
            }
        } catch (error) {
            console.error('Error removing meal:', error);
            showErrorRefreshingMealToast('Error removing meal. Please refresh the page and try again.');
        }
    };

    const handleServingSizeUpdate = async () => {
        try {
            setIsUpdatingServingSize(true);
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF('/api/v1/meal/serving_size_and_refresh', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    meal_id: meal.id,
                    serving_size: servingSize,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to update serving size');
            }

            console.log('Serving size updated:', data);
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification
            if (onGroceryListRefresh) {
                onGroceryListRefresh('Grocery list updated');//onGroceryListRefresh('Serving size and grocery list updated');
            }
        } catch (error) {
            console.error('Error updating serving size:', error);
            showErrorRefreshingMealToast('Error updating serving size. Please refresh the page and try again.');
        } finally {
            setIsUpdatingServingSize(false);
        }
    };

    const handleServingSizeChange = (newSize) => {
        if (newSize >= 1 && newSize <= 20) { // Reasonable limits
            setServingSize(newSize);
        }
    };

    const handleUndoDelete = async (deletedMealObjectArray) => {
        try {
            // Validate that we have the required properties
            for (const deletedMealObject of deletedMealObjectArray) {
                if (!deletedMealObject.meal_plan_id) {
                    throw new Error('Missing meal_plan_id for undo operation');
                }
                if (!deletedMealObject.position) {
                    deletedMealObject.position = null;
                }
                console.log('deletedMealObject.position: ', deletedMealObject.position);
                console.log('deletedMealObject.meal_plan_id: ', deletedMealObject.meal_plan_id);
                console.log('deletedMealObject.meal_of_day: ', deletedMealObject.meal_of_day);
                console.log('deletedMealObject.serving_size: ', deletedMealObject.serving_size);
                console.log('deletedMealObject.recipe_id: ', deletedMealObject.recipe_id);
                await addSingleMealToMealPlan(
                    deletedMealObject.meal_plan_id, 
                    pgUser.id, 
                    deletedMealObject.meal_of_day, 
                    deletedMealObject.serving_size, 
                    deletedMealObject.recipe_id,
                    deletedMealObject.position
                );
            }
            
            // Clear the deleted meals array
            setRecentlyDeletedMealObjectArray([]);
            
            // Refresh the meal plan to show the restored meal
            if (onMealRefresh) {
                onMealRefresh();
            }
            
            // Show success toast
            if (onGroceryListRefresh) {
                onGroceryListRefresh('Grocery list updated');
            }
        } catch (error) {
            console.error('Error undoing delete:', error);
            showErrorRefreshingMealToast('Error undoing delete. Please refresh the page and try again.');
        }
    };

    const handleAddToMealPlanSubmit = async ({ selectedPlannedCookedDate, servings }) => {
        const result = await addRecipeToMealPlan({
            recipeId: meal.recipe_id,
            servings: servings,
            plannedCookDate: selectedPlannedCookedDate
        });
        
        if (result) {
            // Show success toast
            if (onGroceryListRefresh) {
                onGroceryListRefresh('Meal added to meal plan');
            }
        }
        
        return result;
    };

    // Determine card styling based on cooked status and past meal plan
    const cardClassName = `meal-card ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`;
    const buttonClassName = `add-to-meal-plan-btn ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`;
    const buttonText = meal.has_been_cooked ? 'Already Cooked' : isInPast ? 'Past Meal Plan' : 'Start Cooking';

    return (
        <>
            <div className={cardClassName} onClick={handleCardClick} style={{ position: 'relative' }}>
                {/* Delete button in top left corner */}
                {!meal.has_been_cooked && (
                    <div 
                        className={`delete-meal-corner ${isInPast ? 'cooked' : ''}`} 
                        onClick={handleTrashClick}
                        style={{ 
                            position: 'absolute',
                            top: '-8px',
                            left: '-8px',
                            cursor: isInPast ? 'not-allowed' : 'pointer',
                            zIndex: 10,
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'opacity 0.2s, transform 0.2s'
                        }}
                    >
                        <img 
                            src="/icons/x-outline.svg" 
                            alt="Delete Meal" 
                            style={{ 
                                width: '30px', 
                                height: '30px'
                            }}
                        />
                    </div>
                )}
                <h5 >{meal.recipe_name}</h5>
                {/* <div className="d-flex justify-content-end gap-2 mb-2 mt-3">
                    <div 
                        className={`refresh-meal ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`} 
                        onClick={handleRefreshMeal}
                        style={{ 
                            cursor: (meal.has_been_cooked || isInPast) ? 'not-allowed' : 'pointer',
                            opacity: (meal.has_been_cooked || isInPast) ? '0.3' : '0.7'
                        }}
                        title="Refresh meal"
                    >
                        <img 
                            src="/icons/refresh.svg" 
                            alt="Refresh Meal" 
                            style={{ 
                                width: '16px', 
                                height: '16px',
                                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                    </div>
                    <div 
                        className={`remove-meal ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`} 
                        onClick={handleTrashClick}
                        style={{ 
                            cursor: (meal.has_been_cooked || isInPast) ? 'not-allowed' : 'pointer',
                            opacity: (meal.has_been_cooked || isInPast) ? '0.3' : '0.7'
                        }}
                    >
                        <img src="/icons/trash.svg" alt="Edit Meal" />
                    </div>
                </div> */}
                {(!meal.has_been_cooked && isInPast) && (
                    <div className="did-not-cook-badge">
                        <img src="/icons/xmark.svg" alt="Cooked" />
                    </div>
                )}
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <img 
                        src={getRecipeImageUrl(meal.recipe_id)}
                        onError={(e) => {
                            e.target.onerror = null; // Prevent infinite loop if fallback also fails
                            e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                        }}
                        alt={meal.name} // Use recipe name for better accessibility
                        className="meal-card-img"
                        onClick={handleStartCooking}
                    />
                    {(meal.has_been_cooked) && (
                        <div 
                            style={{
                                position: 'absolute',
                                top: '8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#313638',
                                color: '#ffffff',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                zIndex: 5,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            Already Cooked
                        </div>
                    )}
                </div>
                <p className="m-0">{meal.description}</p>
                <div className="mb-1 mt-1">
                    {meal.total_time && (
                        <div className="mb-2" style={{ fontSize: '0.9rem', color: '#666' }}>
                            {meal.total_time} min
                        </div>
                    )}
                    {/* "Swap Out" Button */}
                    <Button
                        variant=""
                        size="sm"
                        onClick={handleRefreshMeal}
                        disabled={meal.has_been_cooked || isInPast}
                        style={{ 
                            border: '1px solid #52cd92',
                            color: '#52cd92',
                            backgroundColor: 'transparent',
                            marginBottom: '10px'
                        }}
                    >
                        <img 
                            src="/icons/refresh_52cd92.svg" 
                            alt="Refresh Meal" 
                            style={{ 
                                width: '18px', 
                                height: '18px',
                                paddingBottom: '2px',
                                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                        <span style={{ fontSize: '0.8rem', color: '#52cd92' }}>  Swap</span>
                    </Button>
                    {meal.serving_size && (
                        <div className="d-flex flex-column align-items-center">
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleServingSizeChange(servingSize - 1);
                                    }}
                                    disabled={meal.has_been_cooked || isInPast || servingSize <= 1}
                                    style={{ minWidth: '1.5rem', height: '1.5rem', padding: '0', fontSize: '0.8rem' }}
                                >
                                    −
                                </Button>
                                <span
                                    style={{
                                        minWidth: '2rem',
                                        textAlign: 'center',
                                        fontSize: '0.9rem',
                                        fontWeight: '500',
                                        opacity: isUpdatingServingSize ? 0.6 : 1
                                    }}
                                >
                                    {servingSize}
                                    {isUpdatingServingSize && <span style={{ fontSize: '0.7rem', marginLeft: '2px' }}>⟳</span>}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleServingSizeChange(servingSize + 1);
                                    }}
                                    disabled={meal.has_been_cooked || isInPast || servingSize >= 20}
                                    style={{ minWidth: '1.5rem', height: '1.5rem', padding: '0', fontSize: '0.8rem' }}
                                >
                                    +
                                </Button>
                            </div>
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>servings</span>
                        </div>
                    )}
                    {/* Add to Meal Plan button - only show for past meals */}
                    
                </div>
                {isInPast && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowAddToMealPlanModal(true);
                            }}
                            style={{ 
                                backgroundColor: '#52cd92',
                                borderColor: '#52cd92',
                                marginTop: '10px',
                                fontSize: '1rem'
                            }}
                        >
                            Add to Meal Plan
                        </Button>
                )}
                
                {/* <button 
                    className={buttonClassName} 
                    onClick={handleStartCooking}
                    disabled={meal.has_been_cooked || isInPast}
                >
                    {buttonText}
                </button> */}
            </div>

            {/* Error Refreshing Toast Notification */}
            <Toast 
                show={errorRefreshingMeal} 
                onClose={() => setErrorRefreshingMeal(false)}
                style={{
                    position: 'fixed',
                    bottom: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#dc3545',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,255,0.15)',
                    width: '50%'
                }}
            >
                <Toast.Header style={{ 
                    backgroundColor: '#dc3545', 
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%'
                }}>
                    <strong style={{ color: 'white' }}>{errorRefreshingMealMessage}</strong>
                </Toast.Header>
            </Toast>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove {meal.recipe_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove {meal.recipe_name} from your meal plan?</p>
                </Modal.Body>
                <Modal.Footer style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div className="d-flex justify-content-end gap-2 mb-2">
                        <Button variant="secondary" onClick={handleModalClose}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm}>
                            Remove
                        </Button>
                    </div>
                    {(showModal || showDeletionConfirmation) && (
                        <div style={{ textAlign: 'left' }}>
                            <Form.Check
                                type="checkbox"
                                label="Don't ask again"
                                className="custom-checkbox"
                                checked={!showDeletionConfirmation}
                                onChange={(e) => {
                                    const shouldShowConfirmation = !e.target.checked;
                                    // Update local state immediately for responsive UI
                                    setShowDeletionConfirmation(shouldShowConfirmation);
                                    // Update global pgUser so all MealCard instances get the new value
                                    updatePgUserProperty('show_meal_deletion_confirmation_modal', shouldShowConfirmation);
                                    // Update backend
                                    updateUserShowMealDeletionConfirmation(shouldShowConfirmation);
                                }}
                            />
                        </div>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Add to Meal Plan Modal */}
            <AddToMealPlanModal
                show={showAddToMealPlanModal}
                onHide={() => setShowAddToMealPlanModal(false)}
                recipe={{ name: meal.recipe_name, id: meal.recipe_id }}
                servings={addToMealPlanServings}
                setServings={setAddToMealPlanServings}
                onSubmit={handleAddToMealPlanSubmit}
                isLoading={isAddingToMealPlan}
                error={addToMealPlanError}
            />
        </>

        
    );
}

export default MealCard;
